import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Lightbulb as HintIcon,
  PlayArrow as RunIcon,
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  ArrowBackIos as PrevIcon,
  Refresh as ResetIcon,
  Check as CompleteIcon,
  TipsAndUpdates as TipIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateExerciseProgress } from '../../store/progressSlice';
import { TestCase } from '../../types';

interface ExerciseRunnerProps {
  courseId: string;
  moduleId: string;
  exerciseId: string;
}

const ExerciseRunner: React.FC<ExerciseRunnerProps> = ({ courseId, moduleId, exerciseId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const course = useAppSelector(state => state.courses[courseId]);
  const progress = useAppSelector(state => state.progress.courseProgress[courseId]);

  const module = course?.modules.find(m => m.id === moduleId);
  const exercise = module?.exercises.find(ex => ex.id === exerciseId);
  const exerciseProgress = progress?.exerciseProgress[exerciseId];

  const [userAnswer, setUserAnswer] = useState('');
  const [testResults, setTestResults] = useState<
    Array<{
      testCase: TestCase;
      passed: boolean;
      error?: string;
    }>
  >([]);
  const [showHints, setShowHints] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(exerciseProgress?.hintsUsed || 0);
  const [visibleHints, setVisibleHints] = useState(0);
  const [attempts, setAttempts] = useState(exerciseProgress?.attempts || 0);
  const [isCompleted, setIsCompleted] = useState(exerciseProgress?.completed || false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [solutionRevealed, setSolutionRevealed] = useState(false);

  useEffect(() => {
    // Reset state when exercise changes
    setUserAnswer('');
    setTestResults([]);
    setShowHints(false);
    setIsRetrying(false);
    setVisibleHints(0);

    // Load exercise-specific data from current progress
    const currentProgress = progress?.exerciseProgress[exerciseId];
    if (currentProgress) {
      setHintsUsed(currentProgress.hintsUsed || 0);
      setAttempts(currentProgress.attempts || 0);
      setIsCompleted(currentProgress.completed || false);
      // Show hints that were already used
      setVisibleHints(currentProgress.hintsUsed || 0);
      if (currentProgress.hintsUsed && currentProgress.hintsUsed > 0) {
        setShowHints(true);
      }

      // Handle solution visibility based on progress
      if (currentProgress.completed && exercise?.solution && !isRetrying) {
        // Exercise is completed - always show solution
        setUserAnswer(exercise.solution);
        setShowSolution(true);
        if (currentProgress.solutionRevealed) {
          setSolutionRevealed(true);
        } else {
          setSolutionRevealed(false);
        }
      } else if (currentProgress.solutionRevealed && exercise?.solution && !isRetrying) {
        // Exercise not completed but solution was manually revealed - preserve it
        setShowSolution(true);
        setSolutionRevealed(true);
      } else {
        // Reset solution visibility if not completed and not manually revealed
        setShowSolution(false);
        setSolutionRevealed(false);
      }
    } else {
      // Reset to defaults for new exercise
      setHintsUsed(0);
      setAttempts(0);
      setIsCompleted(false);
      setVisibleHints(0);
      setShowSolution(false);
      setSolutionRevealed(false);
    }
  }, [exerciseId, exercise?.solution, isRetrying, progress?.exerciseProgress]);

  const handleReset = () => {
    setIsRetrying(true);
    setUserAnswer('');
    setTestResults([]);
    setShowHints(false);
    setVisibleHints(0);
    setIsCompleted(false);
    setShowSolution(false);
  };

  const revealSolution = () => {
    setShowSolution(true);
    setSolutionRevealed(true);
    // Track that solution was revealed
    dispatch(
      updateExerciseProgress({
        courseId,
        exerciseId,
        progress: {
          completed: isCompleted,
          attempts: attempts,
          hintsUsed,
          solutionRevealed: true,
        },
      })
    );
  };

  if (!course || !module || !exercise) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Typography variant='h4'>Exercise not found</Typography>
      </Container>
    );
  }

  const runTests = () => {
    if (!exercise.testCases || !userAnswer.trim()) {
      return;
    }

    const results = exercise.testCases.map(testCase => {
      try {
        let passed = false;

        if (exercise.type === 'regex') {
          const regex = new RegExp(userAnswer.trim());
          const match = regex.test(testCase.input);
          passed = match === testCase.expected;
        } else if (exercise.type === 'multiple-choice') {
          passed = userAnswer.trim() === exercise.correctAnswer?.toString();
        }

        return {
          testCase,
          passed,
        };
      } catch (error) {
        return {
          testCase,
          passed: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    });

    setTestResults(results);

    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    const allPassed = results.every(r => r.passed);
    if (allPassed) {
      setIsCompleted(true);
      setIsRetrying(false);
      dispatch(
        updateExerciseProgress({
          courseId,
          exerciseId,
          progress: {
            completed: true,
            attempts: newAttempts,
            hintsUsed,
            bestScore: 100,
          },
        })
      );
    } else {
      dispatch(
        updateExerciseProgress({
          courseId,
          exerciseId,
          progress: {
            completed: isCompleted && !isRetrying,
            attempts: newAttempts,
            hintsUsed,
          },
        })
      );
    }
  };

  const useHint = () => {
    if (!exercise?.hints) return;

    if (!showHints) {
      setShowHints(true);
    }

    if (visibleHints < exercise.hints.length) {
      const newVisibleHints = visibleHints + 1;
      setVisibleHints(newVisibleHints);

      if (newVisibleHints > hintsUsed) {
        const newHintsUsed = newVisibleHints;
        setHintsUsed(newHintsUsed);

        dispatch(
          updateExerciseProgress({
            courseId,
            exerciseId,
            progress: {
              completed: isCompleted,
              attempts,
              hintsUsed: newHintsUsed,
            },
          })
        );
      }
    }
  };

  const markAsComplete = () => {
    setIsCompleted(true);
    setIsRetrying(false);
    dispatch(
      updateExerciseProgress({
        courseId,
        exerciseId,
        progress: {
          completed: true,
          attempts: attempts || 1,
          hintsUsed,
          bestScore: 100,
        },
      })
    );
  };

  const exerciseIndex = module.exercises.findIndex(ex => ex.id === exerciseId);
  const previousExercise = module.exercises[exerciseIndex - 1];
  const nextExercise = module.exercises[exerciseIndex + 1];

  const handleBack = () => {
    navigate(`/courses/${courseId}/modules/${moduleId}`);
  };

  const handlePrevious = () => {
    if (previousExercise) {
      navigate(`/courses/${courseId}/modules/${moduleId}/exercises/${previousExercise.id}`);
    }
  };

  const handleNext = () => {
    if (nextExercise) {
      navigate(`/courses/${courseId}/modules/${moduleId}/exercises/${nextExercise.id}`);
    }
  };

  const handleContinue = () => {
    if (nextExercise) {
      navigate(`/courses/${courseId}/modules/${moduleId}/exercises/${nextExercise.id}`);
    } else {
      navigate(`/courses/${courseId}/modules/${moduleId}`);
    }
  };

  const passedTests = testResults.filter(r => r.passed).length;
  const totalTests = testResults.length;
  const successRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Navigation Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button startIcon={<BackIcon />} onClick={handleBack} variant='outlined'>
            Back to {module.title}
          </Button>

          <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Exercise {exerciseIndex + 1} of {module.exercises.length}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<PrevIcon />}
              onClick={handlePrevious}
              disabled={!previousExercise}
              variant='outlined'
              size='small'
            >
              Previous
            </Button>
            <Button
              endIcon={<ForwardIcon />}
              onClick={handleNext}
              disabled={!nextExercise}
              variant='outlined'
              size='small'
            >
              Next
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Exercise Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h3' component='h1' gutterBottom>
          {exercise.title}
        </Typography>

        <Typography variant='body1' color='text.secondary' paragraph>
          {exercise.description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label={exercise.difficulty}
            color={
              exercise.difficulty === 'easy'
                ? 'success'
                : exercise.difficulty === 'medium'
                  ? 'warning'
                  : 'error'
            }
          />
          {isCompleted && <Chip label='Completed' color='success' />}
        </Box>

        {/* Progress Stats */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant='subtitle2' gutterBottom>
            Exercise Stats
          </Typography>
          <Box sx={{ display: 'flex', gap: 4 }}>
            {exercise.type === 'implementation' ? (
              <>
                <Typography variant='body2'>
                  <strong>Status:</strong> {isCompleted ? 'Completed' : 'In Progress'}
                </Typography>
                <Typography variant='body2'>
                  <strong>Hints Used:</strong> {hintsUsed}
                </Typography>
                {solutionRevealed && (
                  <Typography variant='body2'>
                    <strong>Solution:</strong> Revealed
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant='body2'>
                  <strong>Attempts:</strong> {attempts}
                </Typography>
                <Typography variant='body2'>
                  <strong>Hints Used:</strong> {hintsUsed}
                </Typography>
                {solutionRevealed && (
                  <Typography variant='body2'>
                    <strong>Solution:</strong> Revealed
                  </Typography>
                )}
                {testResults.length > 0 && (
                  <Typography variant='body2'>
                    <strong>Success Rate:</strong> {Math.round(successRate)}%
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Exercise Content */}
        <Box sx={{ flexGrow: 1, maxWidth: exercise.testCases ? 'calc(100% - 366px)' : '100%' }}>
          {/* Exercise Instructions for Implementation Type */}
          {exercise.type === 'implementation' && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                Exercise Instructions
              </Typography>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code: ({ children, className }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';
                    const isInline = !className;

                    if (isInline) {
                      return (
                        <Box
                          component='code'
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            bgcolor: 'grey.100',
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                          }}
                        >
                          {children}
                        </Box>
                      );
                    }

                    return (
                      <Box sx={{ my: 2 }}>
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={language || 'python'}
                          PreTag='div'
                          customStyle={{
                            borderRadius: '8px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                          }}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </Box>
                    );
                  },
                  p: ({ children }) => (
                    <Typography variant='body1' paragraph>
                      {children}
                    </Typography>
                  ),
                  h1: ({ children }) => (
                    <Typography variant='h5' component='h2' gutterBottom>
                      {children}
                    </Typography>
                  ),
                  h2: ({ children }) => (
                    <Typography variant='h6' component='h3' gutterBottom sx={{ mt: 2 }}>
                      {children}
                    </Typography>
                  ),
                  ul: ({ children }) => (
                    <Box component='ul' sx={{ pl: 3, mb: 2 }}>
                      {children}
                    </Box>
                  ),
                  li: ({ children }) => (
                    <Typography component='li' variant='body1' sx={{ mb: 0.5 }}>
                      {children}
                    </Typography>
                  ),
                }}
              >
                {exercise.instructions}
              </ReactMarkdown>

              {/* Mark as Complete Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                {!isCompleted ? (
                  <Button
                    variant='contained'
                    startIcon={<CompleteIcon />}
                    onClick={markAsComplete}
                    color='success'
                  >
                    Mark as Complete
                  </Button>
                ) : (
                  <Alert severity='success' sx={{ flexGrow: 1 }}>
                    Exercise completed! You can continue to the next exercise or review your work.
                  </Alert>
                )}

                {isCompleted && (
                  <Button
                    variant='outlined'
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                    color='secondary'
                  >
                    Try Again
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Answer Input for Code/Regex Exercises */}
          {exercise.type !== 'implementation' && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                Your Answer
              </Typography>

              {exercise.type === 'regex' && (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your regular expression pattern:
                </Typography>
              )}

              <TextField
                fullWidth
                multiline={exercise.type === 'code'}
                rows={exercise.type === 'code' ? 6 : 1}
                placeholder={
                  exercise.type === 'regex'
                    ? 'Enter your regex pattern...'
                    : exercise.type === 'code'
                      ? 'Enter your code...'
                      : 'Enter your answer...'
                }
                value={userAnswer}
                onChange={e => setUserAnswer(e.target.value)}
                disabled={isCompleted && !isRetrying}
                sx={{ mb: 2, fontFamily: exercise.type === 'regex' ? 'monospace' : 'inherit' }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant='contained'
                  startIcon={<RunIcon />}
                  onClick={runTests}
                  disabled={!userAnswer.trim() || (isCompleted && !isRetrying)}
                >
                  Test Answer
                </Button>

                {isCompleted && !isRetrying && (
                  <Button
                    variant='outlined'
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                    color='secondary'
                  >
                    Try Again
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Test Results */}
          {testResults.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                Test Results
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  Passed {passedTests} of {totalTests} tests
                </Typography>
                <LinearProgress
                  variant='determinate'
                  value={successRate}
                  color={successRate === 100 ? 'success' : 'primary'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <List dense>
                {testResults.map((result, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {result.passed ? <CheckIcon color='success' /> : <CancelIcon color='error' />}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.testCase.description}
                      secondary={
                        <Box>
                          <Typography variant='caption' display='block'>
                            Input: &ldquo;{result.testCase.input}&rdquo; → Expected:{' '}
                            {result.testCase.expected.toString()}
                          </Typography>
                          {result.error && (
                            <Typography variant='caption' color='error'>
                              Error: {result.error}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {isCompleted && (
                <Alert severity='success' sx={{ mt: 2 }}>
                  Congratulations! You&apos;ve completed this exercise successfully.
                  <Button variant='contained' sx={{ ml: 2 }} onClick={handleContinue}>
                    Continue
                  </Button>
                </Alert>
              )}
            </Paper>
          )}
        </Box>

        {/* Sidebar - Only show if there are test cases */}
        {exercise.testCases && (
          <Box sx={{ width: 350 }}>
            {/* Test Cases Preview */}
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Test Cases
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                  Your solution will be tested against these cases:
                </Typography>
                <List dense>
                  {exercise.testCases.slice(0, 3).map((testCase, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={`"${testCase.input}"`}
                        secondary={`Expected: ${testCase.expected}`}
                        primaryTypographyProps={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                        }}
                      />
                    </ListItem>
                  ))}
                  {exercise.testCases.length > 3 && (
                    <ListItem sx={{ pl: 0 }}>
                      <ListItemText secondary={`... and ${exercise.testCases.length - 3} more`} />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Hints Section - Full Width Below Main Content */}
      {exercise.hints && exercise.hints.length > 0 && (
        <Paper sx={{ p: 4, mt: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TipIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant='h5' sx={{ flexGrow: 1 }}>
              Hints & Guidance
            </Typography>
            <Chip
              label={`${visibleHints} of ${exercise.hints.length} hints revealed`}
              color='primary'
              variant={visibleHints === exercise.hints.length ? 'filled' : 'outlined'}
            />
          </Box>

          {/* Show hint button if no hints revealed yet */}
          {visibleHints === 0 && (
            <Card sx={{ bgcolor: 'background.paper', mb: 3 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <HintIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant='h6' gutterBottom>
                  Need a little help getting started?
                </Typography>
                <Typography variant='body1' color='text.secondary' paragraph>
                  We have {exercise.hints.length} hints available to guide you through this
                  exercise. Each hint will provide progressively more specific guidance.
                </Typography>
                <Button
                  variant='contained'
                  startIcon={<HintIcon />}
                  onClick={useHint}
                  size='large'
                  disabled={isCompleted && !isRetrying}
                >
                  Show First Hint
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Display revealed hints */}
          {visibleHints > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {exercise.hints.slice(0, visibleHints).map((hint, index) => (
                <Card
                  key={index}
                  elevation={1}
                  sx={{
                    bgcolor: 'background.paper',
                    borderLeft: 4,
                    borderLeftColor: 'primary.main',
                  }}
                >
                  <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant='subtitle2' color='primary' gutterBottom>
                        HINT {index + 1}
                      </Typography>
                      <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                        {hint}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {/* Show next hint button */}
              {visibleHints < exercise.hints.length && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant='contained'
                    startIcon={<HintIcon />}
                    onClick={useHint}
                    size='large'
                    color='primary'
                  >
                    Show Next Hint ({exercise.hints.length - visibleHints} remaining)
                  </Button>
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    Try to solve it with the current hints before revealing more
                  </Typography>
                </Box>
              )}

              {/* All hints revealed message */}
              {visibleHints === exercise.hints.length && (
                <Card sx={{ bgcolor: 'success.light', borderColor: 'success.main' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CheckIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                    <Typography variant='h6' color='success.dark' gutterBottom>
                      All hints revealed!
                    </Typography>
                    <Typography variant='body1'>
                      You now have all the guidance available. Take your time to work through the
                      solution.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Solution Section - Full Width Below Hints */}
      {exercise.solution && (
        <Paper sx={{ p: 4, mt: 4, bgcolor: showSolution ? '#fff3e0' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <VisibilityIcon
              sx={{ mr: 2, fontSize: 32, color: showSolution ? 'warning.main' : 'text.secondary' }}
            />
            <Typography variant='h5' sx={{ flexGrow: 1 }}>
              Example Solution
            </Typography>
            {showSolution && solutionRevealed && !isCompleted && (
              <Chip label='Revealed before completion' color='warning' size='small' />
            )}
          </Box>

          {!showSolution ? (
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant='contained'
                startIcon={<VisibilityIcon />}
                onClick={revealSolution}
                size='large'
                color='warning'
                disabled={isCompleted}
              >
                {isCompleted ? 'Solution Already Viewed' : 'Reveal Solution'}
              </Button>
            </Box>
          ) : (
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                {solutionRevealed && !isCompleted && (
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    <Typography variant='body2'>
                      You&apos;ve revealed the solution before completing the exercise. Try to
                      understand the approach and implement your own version!
                    </Typography>
                  </Alert>
                )}
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language='python'
                    PreTag='div'
                    customStyle={{
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      margin: 0,
                    }}
                  >
                    {exercise.solution}
                  </SyntaxHighlighter>
                </Box>
                {!isCompleted && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary' paragraph>
                      Now that you&apos;ve seen the solution, try implementing it yourself!
                    </Typography>
                    <Button
                      variant='outlined'
                      onClick={() => setShowSolution(false)}
                      sx={{ mr: 2 }}
                    >
                      Hide Solution
                    </Button>
                    {exercise.type === 'implementation' && (
                      <Button
                        variant='contained'
                        startIcon={<CompleteIcon />}
                        onClick={markAsComplete}
                        color='success'
                      >
                        Mark as Complete
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default ExerciseRunner;
